import { useState, useEffect } from "react";
import {
  Plus,
  Search,
  Check,
  X,
  FileText,
  Trash2,
  Filter,
  ArrowRight,
  ArrowLeft,
  BookOpen,
  Info,
  ExternalLink,
  RefreshCw,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { Button } from "@/components/admin/ui/Button";
import { Input } from "@/components/admin/ui/Input";
import { Badge } from "@/components/admin/ui/Badge";
import { useToast } from "@/components/admin/ui/Toast";

interface ModelTest {
  id: number;
  title: string;
  duration_minutes: number;
  total_marks: number;
  negative_mark: number;
  academic_level: string;
  academic_group: string;
  start_time: string;
  end_time: string;
  status: 'ongoing' | 'upcoming' | 'completed';
  question_count: number;
}

interface Question {
  id: number;
  question_text: string;
  academic_level: string;
  academic_group: string;
  type?: 'mcq' | 'cq';
  options?: string[];
  correct_answer?: string;
  explanation?: string;
  subject_slug?: string;
  chapter_id?: string;
  topic_id?: number;
  stem?: string;
  image_url?: string;
}

interface BoardRef {
  reference_board: string;
  reference_year: string;
}

interface Topic {
  id: number;
  topic_name: string;
  topic_slug: string;
}

interface CustomBuilderQuestion {
  tempId: string;
  type: 'mcq' | 'cq';
  stem: string;
  question_text: string;
  options: string[];
  correct_answer: string;
  explanation: string;
  points: string;
  image_url: string;
  boardRefs: BoardRef[];
  instRefs: string[];
  customInstInput: string;
  
  // UI Expandable Toggles
  showStem: boolean;
  showExplanation: boolean;
  showAdvanced: boolean;

  // CQ subquestions
  cqSubKText: string;
  cqSubKAnswer: string;
  cqSubKhText: string;
  cqSubKhAnswer: string;
  cqSubGText: string;
  cqSubGAnswer: string;
  cqSubGhText: string;
  cqSubGhAnswer: string;
}

export function ModelTestsManager() {
  const [activeTab, setActiveTab] = useState<'tests' | 'question-bank'>('tests');
  const [tests, setTests] = useState<ModelTest[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [search, setSearch] = useState("");
  const { showToast } = useToast();

  // Step 1: Test Details
  const [title, setTitle] = useState("");
  const [duration, setDuration] = useState("30");
  const [marks, setMarks] = useState("100");
  const [negMark, setNegMark] = useState("0.25");
  const [level, setLevel] = useState("ssc");
  const [group, setGroup] = useState("science");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [isPublished, setIsPublished] = useState(true);

  // Step 2: Add Questions States
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<number[]>([]);
  const [questionSearch, setQuestionSearch] = useState("");
  const [activeQuestionMode, setActiveQuestionMode] = useState<'bank' | 'builder' | 'import'>('bank');

  // Question Selector Filters (Sync with Question Bank)
  const [filterSubject, setFilterSubject] = useState("");
  const [filterChapter, setFilterChapter] = useState("");
  const [filterTopic, setFilterTopic] = useState("");
  const [filterTopicsList, setFilterTopicsList] = useState<Topic[]>([]);
  const [isSyncingBank, setIsSyncingBank] = useState(false);

  // Model Question Bank Tab filter states
  const [bankFilterLevel, setBankFilterLevel] = useState("ssc");
  const [bankFilterGroup, setBankFilterGroup] = useState("science");

  // Custom Question Builder Inside Step 2 - Multiple Question Repeater!
  const createBlankCustomQuestion = (): CustomBuilderQuestion => ({
    tempId: Math.random().toString(36).substring(2, 9),
    type: 'mcq',
    stem: '',
    question_text: '',
    options: ['', '', '', ''],
    correct_answer: '0',
    explanation: '',
    points: '1',
    image_url: '',
    boardRefs: [],
    instRefs: [],
    customInstInput: '',
    showStem: false,
    showExplanation: false,
    showAdvanced: false,
    cqSubKText: '',
    cqSubKAnswer: '',
    cqSubKhText: '',
    cqSubKhAnswer: '',
    cqSubGText: '',
    cqSubGAnswer: '',
    cqSubGhText: '',
    cqSubGhAnswer: ''
  });

  const [customBuilderQuestions, setCustomBuilderQuestions] = useState<CustomBuilderQuestion[]>([
    createBlankCustomQuestion()
  ]);

  const [isSavingQuestion, setIsSavingQuestion] = useState(false);

  const shLmsAdmin = (window as any).shLmsAdmin;
  const restUrl = shLmsAdmin?.rest_url || "/wp-json";
  const restNonce = shLmsAdmin?.rest_nonce || shLmsAdmin?.nonce;
  const syllabus = shLmsAdmin?.syllabus_schema || {};
  
  const [routinePdf, setRoutinePdf] = useState<string>(shLmsAdmin?.routine_pdf || "");

  const handleUploadRoutine = () => {
    const wp = (window as any).wp;
    if (wp && wp.media) {
      const frame = wp.media({
        title: 'Select or Upload PDF Routine',
        button: {
          text: 'Use this PDF'
        },
        multiple: false,
        library: {
          type: 'application/pdf'
        }
      });
      frame.on('select', async () => {
        const attachment = frame.state().get('selection').first().toJSON();
        const url = attachment.url;
        setRoutinePdf(url);
        
        // Save via AJAX
        try {
          const ajaxUrl = (window as any).ajaxurl || "/wp-admin/admin-ajax.php";
          const formData = new URLSearchParams();
          formData.append("action", "chorcha_admin_save_routine_pdf");
          formData.append("nonce", shLmsAdmin?.nonce || "");
          formData.append("routine_pdf", url);

          const res = await fetch(ajaxUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/x-www-form-urlencoded"
            },
            body: formData.toString()
          });
          const data = await res.json();
          if (data.success) {
            showToast("Routine PDF saved successfully", "success");
          } else {
            showToast(data.data || "Failed to save routine", "error");
          }
        } catch (err) {
          console.error(err);
          showToast("Server error saving routine PDF", "error");
        }
      });
      frame.open();
    } else {
      showToast("WordPress media library not available", "error");
    }
  };

  const getHeaders = () => {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (restNonce) {
      headers["X-WP-Nonce"] = restNonce;
    }
    return headers;
  };

  // Helper selectors from syllabus schema
  const getSubjectsForLevelAndGroup = (lvl: string, grp: string) => {
    const subjectsObj = syllabus[lvl] || {};
    return Object.entries(subjectsObj).filter(([_, info]: [string, any]) => {
      const g = (info?.group || "compulsory").toLowerCase();
      return grp === "all" || grp === "general" || g === grp.toLowerCase() || g === "compulsory";
    }).map(([slug, info]: [string, any]) => ({
      slug,
      name: info?.name || slug
    }));
  };

  const getChaptersForLevelAndSubject = (lvl: string, subSlug: string) => {
    if (!subSlug) return [];
    const subjectsObj = syllabus[lvl] || {};
    const subInfo = subjectsObj[subSlug] || {};
    const chaptersObj = subInfo.chapters || {};
    return Object.entries(chaptersObj).map(([id, chData]: [string, any]) => {
      const chName = typeof chData === "string" ? chData : (chData?.name || id);
      return { id, name: chName };
    });
  };

  const fetchTests = async () => {
    try {
      setLoading(true);
      // Attempt 1: REST API
      const res = await fetch(`${restUrl}/shuddhoshikha/v1/admin/model-tests`, {
        headers: getHeaders()
      });
      if (res.status === 404) {
        throw new Error("REST Route Not Found (404)");
      }
      const data = await res.json();
      if (data.success && data.exams) {
        setTests(data.exams);
      } else {
        throw new Error(data.message || "Failed to fetch model tests via REST");
      }
    } catch (e) {
      console.warn("REST Model Tests fetch failed, trying Admin AJAX fallback...", e);
      try {
        // Attempt 2: Admin AJAX Fallback
        const ajaxUrl = (window as any).ajaxurl || "/wp-admin/admin-ajax.php";
        const formData = new URLSearchParams();
        formData.append("action", "chorcha_admin_get_model_tests");
        formData.append("nonce", shLmsAdmin?.nonce || "");

        const ajaxRes = await fetch(ajaxUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded"
          },
          body: formData.toString()
        });
        const ajaxData = await ajaxRes.json();
        if (ajaxData.success && ajaxData.data?.exams) {
          setTests(ajaxData.data.exams);
        }
      } catch (ajaxErr) {
        console.error("Admin AJAX Fallback failed to load model tests", ajaxErr);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestionsList = async () => {
    try {
      // Attempt 1: REST API
      const res = await fetch(`${restUrl}/shuddhoshikha/v1/admin/questions-list?academic_level=${bankFilterLevel}&academic_group=${bankFilterGroup}`, {
        headers: getHeaders()
      });
      if (res.status === 404) {
        throw new Error("REST Route Not Found (404)");
      }
      const data = await res.json();
      if (data.success && data.questions) {
        setQuestions(data.questions);
      } else {
        throw new Error(data.message || "Failed to fetch questions via REST");
      }
    } catch (e) {
      console.warn("REST Questions List fetch failed, trying Admin AJAX fallback...", e);
      try {
        // Attempt 2: Admin AJAX Fallback
        const ajaxUrl = (window as any).ajaxurl || "/wp-admin/admin-ajax.php";
        const formData = new URLSearchParams();
        formData.append("action", "chorcha_admin_get_questions_list");
        formData.append("nonce", shLmsAdmin?.nonce || "");
        formData.append("academic_level", bankFilterLevel);
        formData.append("academic_group", bankFilterGroup);

        const ajaxRes = await fetch(ajaxUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded"
          },
          body: formData.toString()
        });
        const ajaxData = await ajaxRes.json();
        if (ajaxData.success && ajaxData.data?.questions) {
          setQuestions(ajaxData.data.questions);
        }
      } catch (ajaxErr) {
        console.error("Admin AJAX Fallback failed to load questions list", ajaxErr);
      }
    }
  };

  useEffect(() => {
    fetchTests();
  }, []);

  useEffect(() => {
    fetchQuestionsList();
  }, [bankFilterLevel, bankFilterGroup]);

  // Fetch topics for filter/builder
  const handleFetchTopics = async (chapterId: string, isFilter: boolean) => {
    if (!chapterId.trim()) return;
    try {
      const res = await fetch(`${restUrl}/shuddhoshikha-hub/v1/topics?chapter_id=${encodeURIComponent(chapterId)}`, {
        headers: getHeaders()
      });
      const data = await res.json();
      if (data.success && data.topics) {
        if (isFilter) {
          setFilterTopicsList(data.topics);
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Sync Questions Bank with Filters
  const handleSyncQuestions = async () => {
    try {
      setIsSyncingBank(true);
      let query = `?academic_level=${level}`;
      if (filterSubject) query += `&subject_slug=${encodeURIComponent(filterSubject)}`;
      if (filterChapter) query += `&chapter_id=${encodeURIComponent(filterChapter)}`;
      if (filterTopic) query += `&topic_id=${encodeURIComponent(filterTopic)}`;

      const res = await fetch(`${restUrl}/shuddhoshikha/v1/admin/questions-list${query}`, {
        headers: getHeaders()
      });
      const data = await res.json();
      if (data.success && data.questions) {
        setQuestions(data.questions);
        showToast(`Loaded ${data.questions.length} questions from Bank`, "success");
      }
    } catch (e) {
      showToast("Failed to load questions from Bank", "error");
    } finally {
      setIsSyncingBank(false);
    }
  };

  // Add Question Card to custom builder list
  const handleAddQuestionCard = () => {
    setCustomBuilderQuestions(prev => [...prev, createBlankCustomQuestion()]);
  };

  // Remove Question Card from custom builder list
  const handleRemoveQuestionCard = (tempId: string) => {
    setCustomBuilderQuestions(prev => prev.filter(q => q.tempId !== tempId));
  };

  // Update specific question field in builder list
  const updateQuestionField = (tempId: string, field: keyof CustomBuilderQuestion, val: any) => {
    setCustomBuilderQuestions(prev => prev.map(q => {
      if (q.tempId === tempId) {
        return { ...q, [field]: val };
      }
      return q;
    }));
  };

  // Save all custom questions to database in one go!
  const handleCreateQuestions = async (): Promise<boolean> => {
    // Validate all questions first
    for (let i = 0; i < customBuilderQuestions.length; i++) {
      const q = customBuilderQuestions[i];
      if (q.type === "mcq") {
        if (!q.question_text.trim()) {
          showToast(`Question ${i + 1}: MCQ Question text is required`, "warning");
          return false;
        }
        if (q.options.some(opt => !opt.trim())) {
          showToast(`Question ${i + 1}: Please fill all MCQ options`, "warning");
          return false;
        }
      } else {
        if (!q.cqSubKText.trim() || !q.cqSubKhText.trim() || !q.cqSubGText.trim() || !q.cqSubGhText.trim()) {
          showToast(`Question ${i + 1}: All subquestions (ক, খ, গ, ঘ) are required for CQ`, "warning");
          return false;
        }
      }
    }

    try {
      setIsSavingQuestion(true);
      const newlyCreatedQuestions: Question[] = [];
      const newlyCreatedIds: number[] = [];

      for (let i = 0; i < customBuilderQuestions.length; i++) {
        const q = customBuilderQuestions[i];
        
        const examRefs = [
          ...q.boardRefs.map(b => ({ reference_board: b.reference_board, reference_year: b.reference_year })),
          ...q.instRefs.map(inst => ({ reference_board: inst, reference_year: "" }))
        ];

        // Default Subject and Chapter to 'general' if not pre-selected in filter
        const targetSubject = filterSubject || 'general';
        const targetChapter = filterChapter || 'general';

        const payload: Record<string, any> = {
          type: q.type,
          stem: q.stem,
          explanation: q.explanation,
          image_url: q.image_url,
          academic_level: level,
          academic_group: group,
          subject_slug: targetSubject,
          chapter_id: targetChapter,
          topic_id: filterTopic ? parseInt(filterTopic, 10) : 0,
          exam_references: JSON.stringify(examRefs)
        };

        if (q.type === "mcq") {
          payload.question_text = q.question_text;
          payload.options = q.options;
          payload.correct_answer = q.correct_answer;
        } else {
          payload.sub_questions = {
            "ক": { text: q.cqSubKText, marks: 1, sub_question_answer: q.cqSubKAnswer },
            "খ": { text: q.cqSubKhText, marks: 2, sub_question_answer: q.cqSubKhAnswer },
            "গ": { text: q.cqSubGText, marks: 3, sub_question_answer: q.cqSubGAnswer },
            "ঘ": { text: q.cqSubGhText, marks: 4, sub_question_answer: q.cqSubGhAnswer }
          };
        }

        let success = false;
        let questionId: number = 0;

        try {
          // Attempt 1: REST API
          const res = await fetch(`${restUrl}/shuddhoshikha/v1/admin/questions`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify(payload)
          });
          
          if (res.status === 404) {
            throw new Error("REST Route Not Found (404)");
          }

          const data = await res.json();
          if (data.success) {
            success = true;
            questionId = data.question_id || data.id;
          } else {
            throw new Error(data.message || `Failed to save via REST`);
          }
        } catch (restErr) {
          console.warn("REST Question Creation failed, trying Admin AJAX fallback...", restErr);
          
          // Attempt 2: Admin AJAX Fallback
          const ajaxUrl = (window as any).ajaxurl || "/wp-admin/admin-ajax.php";
          const formData = new URLSearchParams();
          formData.append("action", "chorcha_admin_save_question");
          formData.append("nonce", shLmsAdmin?.nonce || "");
          formData.append("type", q.type);
          formData.append("stem", q.stem);
          formData.append("explanation", q.explanation);
          formData.append("image_url", q.image_url);
          formData.append("academic_level", level);
          formData.append("academic_group", group);
          formData.append("subject_slug", targetSubject);
          formData.append("chapter_id", targetChapter);
          formData.append("topic_id", filterTopic ? String(filterTopic) : "0");
          formData.append("exam_references", JSON.stringify(examRefs));

          if (q.type === "mcq") {
            formData.append("question_text", q.question_text);
            q.options.forEach((opt, oIdx) => {
              formData.append(`options[${oIdx}]`, opt);
            });
            formData.append("correct_answer", q.correct_answer);
          } else {
            formData.append("cq_title", "Creative Question");
            formData.append("sub_questions[ক][text]", q.cqSubKText);
            formData.append("sub_questions[ক][marks]", "1");
            formData.append("sub_questions[ক][sub_question_answer]", q.cqSubKAnswer);

            formData.append("sub_questions[খ][text]", q.cqSubKhText);
            formData.append("sub_questions[খ][marks]", "2");
            formData.append("sub_questions[খ][sub_question_answer]", q.cqSubKhAnswer);

            formData.append("sub_questions[গ][text]", q.cqSubGText);
            formData.append("sub_questions[গ][marks]", "3");
            formData.append("sub_questions[গ][sub_question_answer]", q.cqSubGAnswer);

            formData.append("sub_questions[ঘ][text]", q.cqSubGhText);
            formData.append("sub_questions[ঘ][marks]", "4");
            formData.append("sub_questions[ঘ][sub_question_answer]", q.cqSubGhAnswer);
          }

          const ajaxRes = await fetch(ajaxUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/x-www-form-urlencoded"
            },
            body: formData.toString()
          });

          const ajaxData = await ajaxRes.json();
          if (ajaxData.success) {
            success = true;
            // Admin AJAX returns new ID inside data object/payload
            questionId = ajaxData.data?.question_id || ajaxData.data?.id || ajaxData.data;
          } else {
            throw new Error(ajaxData.data || "Failed to save via Admin AJAX");
          }
        }

        if (success && questionId > 0) {
          newlyCreatedIds.push(questionId);
          newlyCreatedQuestions.push({
            id: questionId,
            question_text: q.type === "mcq" ? q.question_text : JSON.stringify({
              "ক": { text: q.cqSubKText, marks: 1, sub_question_answer: q.cqSubKAnswer },
              "খ": { text: q.cqSubKhText, marks: 2, sub_question_answer: q.cqSubKhAnswer },
              "গ": { text: q.cqSubGText, marks: 3, sub_question_answer: q.cqSubGAnswer },
              "ঘ": { text: q.cqSubGhText, marks: 4, sub_question_answer: q.cqSubGhAnswer }
            }),
            academic_level: level,
            academic_group: group,
            type: q.type,
            options: q.type === "mcq" ? q.options : [],
            correct_answer: q.type === "mcq" ? q.options[parseInt(q.correct_answer, 10)] : "Creative",
            explanation: q.explanation,
            subject_slug: targetSubject,
            chapter_id: targetChapter,
            topic_id: filterTopic ? parseInt(filterTopic, 10) : undefined
          });
        } else {
          throw new Error(`Failed to save Question ${i + 1}`);
        }
      }

      showToast(`Successfully created and synced ${newlyCreatedQuestions.length} questions!`, "success");

      // Update question list state and select them
      setQuestions(prev => [...newlyCreatedQuestions, ...prev]);
      setSelectedQuestionIds(prev => [...prev, ...newlyCreatedIds]);

      // Reset builder list to single blank question
      setCustomBuilderQuestions([createBlankCustomQuestion()]);
      
      // Navigate back to Bank tab to review selections
      setActiveQuestionMode('bank');
      return true;

    } catch (e: any) {
      showToast(e.message || "Server error saving questions", "error");
      return false;
    } finally {
      setIsSavingQuestion(false);
    }
  };

  const handleCreateTest = async () => {
    if (!title || !startTime || !endTime) {
      showToast("Please fill all required fields", "warning");
      return;
    }

    let success = false;

    try {
      // Attempt 1: REST API
      const res = await fetch(`${restUrl}/shuddhoshikha/v1/admin/model-tests`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({
          title,
          duration_minutes: parseInt(duration, 10),
          total_marks: parseFloat(marks),
          negative_mark: parseFloat(negMark),
          academic_level: level,
          academic_group: group,
          start_time: startTime.replace("T", " "),
          end_time: endTime.replace("T", " "),
          is_published: isPublished ? 1 : 0,
          question_ids: selectedQuestionIds
        })
      });

      if (res.status === 404) {
        throw new Error("REST Route Not Found (404)");
      }

      const data = await res.json();
      if (data.success) {
        success = true;
      } else {
        throw new Error(data.message || "Failed to save via REST");
      }
    } catch (restErr) {
      console.warn("REST Model Test publication failed, trying Admin AJAX fallback...", restErr);

      try {
        // Attempt 2: Admin AJAX Fallback
        const ajaxUrl = (window as any).ajaxurl || "/wp-admin/admin-ajax.php";
        const formData = new URLSearchParams();
        formData.append("action", "chorcha_admin_save_model_test");
        formData.append("nonce", shLmsAdmin?.nonce || "");
        formData.append("title", title);
        formData.append("duration_minutes", String(parseInt(duration, 10)));
        formData.append("total_marks", String(parseFloat(marks)));
        formData.append("negative_mark", String(parseFloat(negMark)));
        formData.append("academic_level", level);
        formData.append("academic_group", group);
        formData.append("start_time", startTime.replace("T", " "));
        formData.append("end_time", endTime.replace("T", " "));
        formData.append("is_published", isPublished ? "1" : "0");
        formData.append("question_ids", JSON.stringify(selectedQuestionIds));

        const ajaxRes = await fetch(ajaxUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded"
          },
          body: formData.toString()
        });

        const ajaxData = await ajaxRes.json();
        if (ajaxData.success) {
          success = true;
        } else {
          throw new Error(ajaxData.data || "Failed to publish via Admin AJAX");
        }
      } catch (ajaxErr: any) {
        showToast(ajaxErr.message || "Server error occurred", "error");
        return;
      }
    }

    if (success) {
      showToast("Model Test published successfully", "success");
      setShowWizard(false);
      setWizardStep(1);
      fetchTests();
      // Reset form
      setTitle("");
      setStartTime("");
      setEndTime("");
      setSelectedQuestionIds([]);
    }
  };

  const toggleQuestionSelect = (id: number) => {
    setSelectedQuestionIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // Helper parser for displaying MCQ / CQ text in table
  const renderQuestionText = (q: Question) => {
    if (q.type === "cq") {
      try {
        const parsed = JSON.parse(q.question_text);
        return (
          <div className="space-y-1 my-1">
            <Badge variant="blue" className="mb-1 text-[9px]">CQ Layout</Badge>
            {parsed["ক"] && <div><span className="font-bold text-[#00CBA9] mr-1">ক:</span>{parsed["ক"].text}</div>}
            {parsed["খ"] && <div><span className="font-bold text-[#00CBA9] mr-1">খ:</span>{parsed["খ"].text}</div>}
            {parsed["গ"] && <div><span className="font-bold text-[#00CBA9] mr-1">গ:</span>{parsed["গ"].text}</div>}
            {parsed["ঘ"] && <div><span className="font-bold text-[#00CBA9] mr-1">ঘ:</span>{parsed["ঘ"].text}</div>}
          </div>
        );
      } catch (e) {
        return q.question_text;
      }
    }
    return q.question_text;
  };

  const handleNextStep = async () => {
    if (wizardStep === 1) {
      if (!title || !startTime || !endTime) {
        showToast("Please fill all required test fields", "warning");
        return;
      }
    }
    if (wizardStep === 2) {
      // Check if there are unsaved questions with draft text
      const hasDraftText = customBuilderQuestions.some(q => 
        q.type === 'mcq' 
          ? q.question_text.trim() !== '' 
          : (q.cqSubKText.trim() !== '' || q.cqSubKhText.trim() !== '' || q.cqSubGText.trim() !== '' || q.cqSubGhText.trim() !== '')
      );

      if (activeQuestionMode === 'builder' && hasDraftText) {
        const saveSuccess = await handleCreateQuestions();
        if (!saveSuccess) {
          return; // Stay on Step 2 so they can fix input issues
        }
      }

      if (selectedQuestionIds.length === 0) {
        showToast("Please select at least 1 question for the model test", "warning");
        return;
      }
    }
    setWizardStep(prev => prev + 1);
  };

  const handleBackStep = () => {
    setWizardStep(prev => prev - 1);
  };

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 font-bangla">চর্চাঘর • মডেল টেস্ট</h1>
          <p className="text-sm text-gray-500 mt-1">Configure exams and manage the question bank</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            leftIcon={<BookOpen className="h-4 w-4" />}
            onClick={handleUploadRoutine}
            className="text-xs"
          >
            {routinePdf ? 'Update Routine PDF' : 'Upload Routine PDF'}
          </Button>

          {activeTab === 'tests' ? (
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => { setWizardStep(1); setShowWizard(true); }}>
              Create Model Test
            </Button>
          ) : (
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => { setWizardStep(2); setShowWizard(true); setActiveQuestionMode('builder'); }}>
              Create Question
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-6 border-b border-gray-200">
        <button
          onClick={() => { setActiveTab('tests'); setSearch(""); }}
          className={`pb-3 px-4 font-bold text-sm transition-all focus:outline-none ${
            activeTab === 'tests'
              ? 'border-b-2 border-[#00CBA9] text-[#00CBA9]'
              : 'text-gray-400 hover:text-gray-600'
          }`}
        >
          Model Tests
        </button>
        <button
          onClick={() => { setActiveTab('question-bank'); setQuestionSearch(""); }}
          className={`pb-3 px-4 font-bold text-sm transition-all focus:outline-none ${
            activeTab === 'question-bank'
              ? 'border-b-2 border-[#00CBA9] text-[#00CBA9]'
              : 'text-gray-400 hover:text-gray-600'
          }`}
        >
          Model Question Bank
        </button>
      </div>

      {activeTab === 'tests' ? (
        /* Model Tests View */
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <div className="relative w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search tests..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 text-xs"
              />
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center text-gray-400">Loading model tests...</div>
          ) : tests.filter(t => t.title.toLowerCase().includes(search.toLowerCase())).length === 0 ? (
            <div className="p-12 text-center text-gray-400">No model tests configured yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-150 text-[10px] uppercase font-bold text-gray-400 tracking-wider">
                    <th className="px-6 py-3">Title</th>
                    <th className="px-6 py-3">Level / Group</th>
                    <th className="px-6 py-3">Duration</th>
                    <th className="px-6 py-3">Marks</th>
                    <th className="px-6 py-3">Timing</th>
                    <th className="px-6 py-3">Questions</th>
                    <th className="px-6 py-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-xs">
                  {tests.filter(t => t.title.toLowerCase().includes(search.toLowerCase())).map((test) => (
                    <tr key={test.id} className="hover:bg-gray-50/50">
                      <td className="px-6 py-4 font-semibold text-gray-800">{test.title}</td>
                      <td className="px-6 py-4 uppercase">
                        {test.academic_level} • {test.academic_group}
                      </td>
                      <td className="px-6 py-4">{test.duration_minutes} mins</td>
                      <td className="px-6 py-4">
                        {test.total_marks} (-{test.negative_mark})
                      </td>
                      <td className="px-6 py-4 text-gray-500">
                        <div>Start: {test.start_time}</div>
                        <div className="mt-0.5">End: {test.end_time}</div>
                      </td>
                      <td className="px-6 py-4 font-bold text-slate-700">{test.question_count} Qs</td>
                      <td className="px-6 py-4">
                        <Badge variant={test.status === "ongoing" ? "green" : test.status === "upcoming" ? "yellow" : "gray"}>
                          {test.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        /* Model Question Bank View */
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between gap-4 flex-wrap">
            <div className="relative w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search questions..."
                value={questionSearch}
                onChange={e => setQuestionSearch(e.target.value)}
                className="pl-9 text-xs"
              />
            </div>
            
            {/* Quick Filters */}
            <div className="flex items-center gap-2 text-xs">
              <Filter className="w-3.5 h-3.5 text-gray-400" />
              <select
                value={bankFilterLevel}
                onChange={e => { setBankFilterLevel(e.target.value); }}
                className="px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white"
              >
                <option value="ssc">SSC</option>
                <option value="hsc">HSC</option>
                <option value="admission">Admission</option>
              </select>
              <select
                value={bankFilterGroup}
                onChange={e => { setBankFilterGroup(e.target.value); }}
                className="px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white"
              >
                <option value="compulsory">Compulsory</option>
                <option value="science">Science</option>
                <option value="humanities">Humanities</option>
                <option value="commerce">Commerce</option>
              </select>
            </div>
          </div>

          {questions.length === 0 ? (
            <div className="p-12 text-center text-gray-400">No questions found in database.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-150 text-[10px] uppercase font-bold text-gray-400 tracking-wider">
                    <th className="px-6 py-3">ID</th>
                    <th className="px-6 py-3">Type</th>
                    <th className="px-6 py-3">Question Text / Details</th>
                    <th className="px-6 py-3">Level / Group</th>
                    <th className="px-6 py-3">Subject / Chapter</th>
                    <th className="px-6 py-3">Correct Answer</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-xs">
                  {questions.filter(q => q.question_text.toLowerCase().includes(questionSearch.toLowerCase())).map((q) => (
                    <tr key={q.id} className="hover:bg-gray-50/50">
                      <td className="px-6 py-4 font-mono text-gray-400">#{q.id}</td>
                      <td className="px-6 py-4 uppercase">
                        <Badge variant={q.type === 'cq' ? 'blue' : 'green'}>
                          {q.type || 'mcq'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 max-w-md">{renderQuestionText(q)}</td>
                      <td className="px-6 py-4 uppercase">{q.academic_level} • {q.academic_group}</td>
                      <td className="px-6 py-4 uppercase text-gray-400">{q.subject_slug || 'General'} • CH {q.chapter_id || '0'}</td>
                      <td className="px-6 py-4 text-gray-700 max-w-xs truncate">{q.correct_answer || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* 4-Step Model Test Builder Wizard Popup */}
      {showWizard && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#f8fafc] border border-gray-200 rounded-2xl shadow-xl w-full max-w-4xl h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            
            {/* Header with Steps Progress indicator */}
            <div className="bg-white border-b border-gray-150 px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#00CBA9]" />
                <h3 className="font-bold text-gray-800">Model Test Builder Wizard</h3>
              </div>

              {/* Step indicator pills */}
              <div className="flex items-center gap-1.5 sm:gap-2.5 text-[10px] uppercase font-bold text-gray-400 tracking-wider">
                <span className={`px-2.5 py-1.5 rounded-lg flex items-center gap-1 ${wizardStep === 1 ? 'bg-[#00CBA9]/10 text-[#00CBA9]' : wizardStep > 1 ? 'text-gray-600' : ''}`}>
                  <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] ${wizardStep >= 1 ? 'bg-[#00CBA9] text-white' : 'bg-gray-200'}`}>1</span>
                  Details
                </span>
                <span className="text-gray-300">/</span>
                <span className={`px-2.5 py-1.5 rounded-lg flex items-center gap-1 ${wizardStep === 2 ? 'bg-[#00CBA9]/10 text-[#00CBA9]' : wizardStep > 2 ? 'text-gray-600' : ''}`}>
                  <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] ${wizardStep >= 2 ? 'bg-[#00CBA9] text-white' : 'bg-gray-200'}`}>2</span>
                  Questions
                </span>
                <span className="text-gray-300">/</span>
                <span className={`px-2.5 py-1.5 rounded-lg flex items-center gap-1 ${wizardStep === 3 ? 'bg-[#00CBA9]/10 text-[#00CBA9]' : wizardStep > 3 ? 'text-gray-600' : ''}`}>
                  <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] ${wizardStep >= 3 ? 'bg-[#00CBA9] text-white' : 'bg-gray-200'}`}>3</span>
                  Revision
                </span>
                <span className="text-gray-300">/</span>
                <span className={`px-2.5 py-1.5 rounded-lg flex items-center gap-1 ${wizardStep === 4 ? 'bg-[#00CBA9]/10 text-[#00CBA9]' : ''}`}>
                  <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] ${wizardStep === 4 ? 'bg-[#00CBA9] text-white' : 'bg-gray-200'}`}>4</span>
                  Finalize
                </span>
              </div>

              <button onClick={() => setShowWizard(false)} className="p-1 text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Wizard Step Content Body */}
            <div className="flex-1 overflow-y-auto p-6">
              
              {/* STEP 1: Test Details */}
              {wizardStep === 1 && (
                <div className="bg-white border border-gray-150 rounded-2xl p-6 shadow-sm space-y-6 max-w-2xl mx-auto">
                  <h4 className="text-sm font-bold text-gray-700 border-b border-gray-100 pb-3 flex items-center gap-1.5">
                    <Info className="w-4 h-4 text-[#00CBA9]" /> Step 1: Configure Test Options & Settings
                  </h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                    <div className="md:col-span-2">
                      <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Test Title *</label>
                      <Input
                        type="text"
                        required
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                        placeholder="Enter model test title..."
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Academic Level</label>
                      <select
                        value={level}
                        onChange={e => setLevel(e.target.value)}
                        className="w-full px-3 py-2 rounded-xl border border-gray-200 bg-white focus:outline-none focus:border-[#00CBA9]"
                      >
                        <option value="ssc">SSC</option>
                        <option value="hsc">HSC</option>
                        <option value="admission">Admission</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Academic Group</label>
                      <select
                        value={group}
                        onChange={e => setGroup(e.target.value)}
                        className="w-full px-3 py-2 rounded-xl border border-gray-200 bg-white focus:outline-none focus:border-[#00CBA9]"
                      >
                        <option value="science">Science</option>
                        <option value="commerce">Commerce</option>
                        <option value="humanities">Humanities</option>
                        <option value="general">General</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Duration (Minutes)</label>
                      <Input
                        type="number"
                        value={duration}
                        onChange={e => setDuration(e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Total Marks</label>
                      <Input
                        type="number"
                        value={marks}
                        onChange={e => setMarks(e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Negative Mark Rate</label>
                      <Input
                        type="number"
                        step="0.05"
                        value={negMark}
                        onChange={e => setNegMark(e.target.value)}
                      />
                    </div>

                    <div className="flex items-center mt-6">
                      <input
                        type="checkbox"
                        id="isPublished"
                        checked={isPublished}
                        onChange={e => setIsPublished(e.target.checked)}
                        className="w-4 h-4 rounded text-[#00CBA9] focus:ring-[#00CBA9]"
                      />
                      <label htmlFor="isPublished" className="ml-2 text-xs font-semibold text-gray-700">Publish Immediately</label>
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Start Time (UTC+6) *</label>
                      <Input
                        type="datetime-local"
                        required
                        value={startTime}
                        onChange={e => setStartTime(e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">End Time (UTC+6) *</label>
                      <Input
                        type="datetime-local"
                        required
                        value={endTime}
                        onChange={e => setEndTime(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 2: Add / Create Questions */}
              {wizardStep === 2 && (
                <div className="space-y-4">
                  {/* Selector Subtabs */}
                  <div className="flex items-center gap-2 border-b border-gray-200 bg-white p-2 rounded-xl border">
                    <button
                      type="button"
                      onClick={() => setActiveQuestionMode('bank')}
                      className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition-all ${
                        activeQuestionMode === 'bank'
                          ? 'bg-[#00CBA9] text-white'
                          : 'text-gray-500 hover:bg-slate-50'
                      }`}
                    >
                      Pick from Question Bank
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveQuestionMode('builder')}
                      className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition-all ${
                        activeQuestionMode === 'builder'
                          ? 'bg-[#00CBA9] text-white'
                          : 'text-gray-500 hover:bg-slate-50'
                      }`}
                    >
                      Create Custom Question
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveQuestionMode('import')}
                      className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition-all ${
                        activeQuestionMode === 'import'
                          ? 'bg-[#00CBA9] text-white'
                          : 'text-gray-500 hover:bg-slate-50'
                      }`}
                    >
                      Bulk Import (Excel)
                    </button>
                  </div>

                  {/* Mode Content: Pick from Question Bank */}
                  {activeQuestionMode === 'bank' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      {/* Sidebar Sync filters */}
                      <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm self-start space-y-4 text-xs">
                        <h4 className="font-bold text-gray-700 flex items-center gap-1"><Filter className="w-3.5 h-3.5 text-[#00CBA9]" /> Filters</h4>
                        
                        <div>
                          <label className="block text-[9px] font-bold text-gray-400 uppercase mb-1">Subject</label>
                          <select
                            value={filterSubject}
                            onChange={e => {
                              setFilterSubject(e.target.value);
                              setFilterChapter("");
                              setFilterTopic("");
                            }}
                            className="w-full px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white"
                          >
                            <option value="">Select Subject</option>
                            {getSubjectsForLevelAndGroup(level, group).map(sub => (
                              <option key={sub.slug} value={sub.slug}>{sub.name}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-[9px] font-bold text-gray-400 uppercase mb-1">Chapter</label>
                          <select
                            value={filterChapter}
                            onChange={e => {
                              setFilterChapter(e.target.value);
                              setFilterTopic("");
                              handleFetchTopics(e.target.value, true);
                            }}
                            disabled={!filterSubject}
                            className="w-full px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white disabled:opacity-50"
                          >
                            <option value="">Select Chapter</option>
                            {getChaptersForLevelAndSubject(level, filterSubject).map(ch => (
                              <option key={ch.id} value={ch.id}>{ch.name}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-[9px] font-bold text-gray-400 uppercase mb-1">Topic</label>
                          <select
                            value={filterTopic}
                            onChange={e => setFilterTopic(e.target.value)}
                            disabled={!filterChapter}
                            className="w-full px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white disabled:opacity-50"
                          >
                            <option value="">All Topics</option>
                            {filterTopicsList.map(t => (
                              <option key={t.id} value={t.id}>{t.topic_name}</option>
                            ))}
                          </select>
                        </div>

                        <Button
                          type="button"
                          variant="secondary"
                          onClick={handleSyncQuestions}
                          disabled={isSyncingBank}
                          className="w-full py-2 bg-slate-50 hover:bg-slate-100 text-xs font-semibold"
                        >
                          {isSyncingBank ? "Syncing..." : "Sync Questions"}
                        </Button>
                      </div>

                      {/* Main checklist table */}
                      <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl p-4 shadow-sm space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="text-xs font-bold text-gray-700">Available Questions ({selectedQuestionIds.length} Selected)</h4>
                          <div className="relative w-48">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                            <Input
                              type="text"
                              placeholder="Search list..."
                              value={questionSearch}
                              onChange={e => setQuestionSearch(e.target.value)}
                              className="pl-8 py-1.5 text-[10px]"
                            />
                          </div>
                        </div>

                        <div className="border border-gray-150 rounded-xl divide-y divide-gray-100 max-h-96 overflow-y-auto text-xs">
                          {questions.filter(q => q.question_text.toLowerCase().includes(questionSearch.toLowerCase())).length === 0 ? (
                            <div className="p-8 text-center text-gray-400">No questions found. Load matching questions via filters on the left.</div>
                          ) : (
                            questions.filter(q => q.question_text.toLowerCase().includes(questionSearch.toLowerCase())).map((q) => {
                              const isSelected = selectedQuestionIds.includes(q.id);
                              return (
                                <div
                                  key={q.id}
                                  onClick={() => toggleQuestionSelect(q.id)}
                                  className={`flex items-start gap-3 p-3 cursor-pointer transition-colors ${
                                    isSelected ? "bg-slate-50 font-semibold" : "hover:bg-slate-50/30"
                                  }`}
                                >
                                  <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                    isSelected ? "bg-[#00CBA9] border-[#00CBA9] text-white" : "border-gray-300 bg-white"
                                  }`}>
                                    {isSelected && <Check className="w-3 h-3 stroke-[3]" />}
                                  </div>
                                  <div className="flex-1">
                                    <div className="text-gray-800">{renderQuestionText(q)}</div>
                                    <span className="text-[9px] uppercase text-gray-400 mt-1 block">
                                      ID: {q.id} • {q.academic_level} • {q.academic_group} • Subject: {q.subject_slug || "None"}
                                    </span>
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Mode Content: Minimalist & Modern Custom Question Builder Repeater */}
                  {activeQuestionMode === 'builder' && (
                    <div className="space-y-4 max-w-3xl mx-auto">
                      {/* Active target banner if category selected */}
                      {filterSubject && filterChapter && (
                        <div className="bg-slate-100 border border-slate-200 text-slate-700 rounded-xl px-4 py-2 text-[10px] uppercase font-bold tracking-wider flex items-center justify-between">
                          <span>🎯 Target Category: Subject: {filterSubject} • Chapter: {filterChapter}</span>
                          <span className="text-gray-400 text-[9px]">Automatically linked</span>
                        </div>
                      )}

                      {/* Stacked Repeater Cards List */}
                      <div className="space-y-4">
                        {customBuilderQuestions.map((q, idx) => (
                          <div key={q.tempId} className="bg-white border border-slate-100 rounded-2xl shadow-sm p-5 space-y-4 text-xs relative">
                            {/* Card sub header / inline inline settings */}
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-50 pb-3">
                              <div className="flex items-center gap-3">
                                <span className="font-extrabold text-slate-800 text-sm">Question {idx + 1}</span>
                                {/* Type Toggle Buttons */}
                                <div className="flex items-center border border-slate-100 rounded-lg p-0.5 bg-slate-50 text-[10px]">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const next = [...customBuilderQuestions];
                                      next[idx].type = 'mcq';
                                      setCustomBuilderQuestions(next);
                                    }}
                                    className={`px-3 py-1 rounded-md font-bold transition-all focus:outline-none ${
                                      q.type === 'mcq' ? 'bg-white shadow-xs text-[#00CBA9]' : 'text-gray-400 hover:text-gray-600'
                                    }`}
                                  >
                                    MCQ
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const next = [...customBuilderQuestions];
                                      next[idx].type = 'cq';
                                      setCustomBuilderQuestions(next);
                                    }}
                                    className={`px-3 py-1 rounded-md font-bold transition-all focus:outline-none ${
                                      q.type === 'cq' ? 'bg-white shadow-xs text-[#00CBA9]' : 'text-gray-400 hover:text-gray-600'
                                    }`}
                                  >
                                    CQ
                                  </button>
                                </div>
                              </div>

                              <div className="flex items-center gap-3">
                                {/* Inline Points */}
                                <div className="flex items-center gap-1">
                                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Marks:</span>
                                  <input
                                    type="number"
                                    value={q.points}
                                    onChange={e => updateQuestionField(q.tempId, "points", e.target.value)}
                                    className="w-12 px-2 py-1 rounded-lg border border-slate-200 bg-slate-50 text-center font-bold text-xs focus:outline-none focus:border-[#00CBA9]"
                                  />
                                </div>

                                {customBuilderQuestions.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveQuestionCard(q.tempId)}
                                    className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                                    title="Remove Question"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Optional Stem / Stimulus trigger */}
                            {!q.showStem ? (
                              <button
                                type="button"
                                onClick={() => updateQuestionField(q.tempId, "showStem", true)}
                                className="text-[10px] font-bold text-slate-400 hover:text-[#00CBA9] transition-colors"
                              >
                                + Add Stimulus (উদ্দীপক / Scenario)
                              </button>
                            ) : (
                              <div className="space-y-1">
                                <div className="flex items-center justify-between">
                                  <label className="block text-[9px] font-bold text-gray-400 uppercase tracking-wider">উদ্দীপক / Stimulus (Supports LaTeX)</label>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      updateQuestionField(q.tempId, "showStem", false);
                                      updateQuestionField(q.tempId, "stem", "");
                                    }}
                                    className="text-[9px] text-slate-400 hover:text-slate-600"
                                  >
                                    Remove
                                  </button>
                                </div>
                                <textarea
                                  placeholder="Enter scenario paragraph or math stem..."
                                  value={q.stem}
                                  onChange={e => updateQuestionField(q.tempId, "stem", e.target.value)}
                                  className="w-full min-h-[60px] p-2.5 border border-slate-200 rounded-xl focus:outline-none focus:border-[#00CBA9] text-xs bg-slate-50/30"
                                />
                              </div>
                            )}

                            {/* MCQ Layout */}
                            {q.type === 'mcq' && (
                              <div className="space-y-4">
                                <div className="space-y-1">
                                  <label className="block text-[9px] font-bold text-gray-400 uppercase tracking-wider">Question Text *</label>
                                  <textarea
                                    placeholder="Enter the MCQ question text..."
                                    value={q.question_text}
                                    onChange={e => updateQuestionField(q.tempId, "question_text", e.target.value)}
                                    className="w-full min-h-[50px] p-2.5 border border-slate-200 rounded-xl focus:outline-none focus:border-[#00CBA9] text-xs"
                                  />
                                </div>

                                {/* Modern clean 2x2 grid options */}
                                <div className="space-y-1">
                                  <label className="block text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Answer Options & Correct Designation *</label>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                                    {q.options.map((opt, oIdx) => {
                                      const isCorrect = q.correct_answer === oIdx.toString();
                                      return (
                                        <div
                                          key={oIdx}
                                          className={`relative flex items-center border rounded-xl px-3 py-2 bg-slate-50/20 hover:border-slate-300 transition-all ${
                                            isCorrect ? 'border-emerald-300 bg-emerald-50/20 hover:border-emerald-400' : 'border-slate-150'
                                          }`}
                                        >
                                          <input
                                            type="radio"
                                            name={`correctSelect-${q.tempId}`}
                                            checked={isCorrect}
                                            onChange={() => updateQuestionField(q.tempId, "correct_answer", oIdx.toString())}
                                            className="text-emerald-500 focus:ring-emerald-400 w-4 h-4 cursor-pointer mr-3"
                                          />
                                          <input
                                            type="text"
                                            placeholder={`Option ${String.fromCharCode(65 + oIdx)}`}
                                            value={opt}
                                            onChange={e => {
                                              const nextOpts = [...q.options];
                                              nextOpts[oIdx] = e.target.value;
                                              updateQuestionField(q.tempId, "options", nextOpts);
                                            }}
                                            className="w-full bg-transparent border-none p-0 text-xs focus:outline-none focus:ring-0 text-slate-800"
                                          />
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* CQ Layout */}
                            {q.type === 'cq' && (
                              <div className="space-y-3 pl-3 border-l-2 border-slate-200 mt-2 bg-slate-50/30 p-3.5 rounded-xl">
                                <h5 className="font-bold text-slate-700 text-[11px] mb-2 uppercase tracking-wide">CQ Subquestions</h5>
                                
                                <div className="space-y-3">
                                  <div>
                                    <div className="flex justify-between text-[9px] font-bold text-slate-500 mb-1">
                                      <span>ক (Knowledge-based Question) [1 Mark]</span>
                                    </div>
                                    <Input
                                      type="text"
                                      placeholder="Question ক text..."
                                      value={q.cqSubKText}
                                      onChange={e => updateQuestionField(q.tempId, "cqSubKText", e.target.value)}
                                      className="py-1 text-xs"
                                    />
                                    <textarea
                                      placeholder="Model answer..."
                                      value={q.cqSubKAnswer}
                                      onChange={e => updateQuestionField(q.tempId, "cqSubKAnswer", e.target.value)}
                                      className="w-full mt-1 p-1.5 border border-slate-200 rounded-lg text-[10px] focus:outline-none"
                                      rows={1}
                                    />
                                  </div>

                                  <div>
                                    <div className="flex justify-between text-[9px] font-bold text-slate-500 mb-1">
                                      <span>খ (Comprehension Question) [2 Marks]</span>
                                    </div>
                                    <Input
                                      type="text"
                                      placeholder="Question খ text..."
                                      value={q.cqSubKhText}
                                      onChange={e => updateQuestionField(q.tempId, "cqSubKhText", e.target.value)}
                                      className="py-1 text-xs"
                                    />
                                    <textarea
                                      placeholder="Model answer..."
                                      value={q.cqSubKhAnswer}
                                      onChange={e => updateQuestionField(q.tempId, "cqSubKhAnswer", e.target.value)}
                                      className="w-full mt-1 p-1.5 border border-slate-200 rounded-lg text-[10px] focus:outline-none"
                                      rows={1}
                                    />
                                  </div>

                                  <div>
                                    <div className="flex justify-between text-[9px] font-bold text-slate-500 mb-1">
                                      <span>গ (Application Question) [3 Marks]</span>
                                    </div>
                                    <Input
                                      type="text"
                                      placeholder="Question গ text..."
                                      value={q.cqSubGText}
                                      onChange={e => updateQuestionField(q.tempId, "cqSubGText", e.target.value)}
                                      className="py-1 text-xs"
                                    />
                                    <textarea
                                      placeholder="Model answer..."
                                      value={q.cqSubGAnswer}
                                      onChange={e => updateQuestionField(q.tempId, "cqSubGAnswer", e.target.value)}
                                      className="w-full mt-1 p-1.5 border border-slate-200 rounded-lg text-[10px] focus:outline-none"
                                      rows={1}
                                    />
                                  </div>

                                  <div>
                                    <div className="flex justify-between text-[9px] font-bold text-slate-500 mb-1">
                                      <span>ঘ (Higher Ability Question) [4 Marks]</span>
                                    </div>
                                    <Input
                                      type="text"
                                      placeholder="Question ঘ text..."
                                      value={q.cqSubGhText}
                                      onChange={e => updateQuestionField(q.tempId, "cqSubGhText", e.target.value)}
                                      className="py-1 text-xs"
                                    />
                                    <textarea
                                      placeholder="Model answer..."
                                      value={q.cqSubGhAnswer}
                                      onChange={e => updateQuestionField(q.tempId, "cqSubGhAnswer", e.target.value)}
                                      className="w-full mt-1 p-1.5 border border-slate-200 rounded-lg text-[10px] focus:outline-none"
                                      rows={1}
                                    />
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Optional Explanation trigger */}
                            {!q.showExplanation ? (
                              <button
                                type="button"
                                onClick={() => updateQuestionField(q.tempId, "showExplanation", true)}
                                className="text-[10px] font-bold text-slate-400 hover:text-[#00CBA9] transition-colors block"
                              >
                                + Add Explanation & Rationale
                              </button>
                            ) : (
                              <div className="space-y-1">
                                <div className="flex items-center justify-between">
                                  <label className="block text-[9px] font-bold text-gray-400 uppercase tracking-wider">Explanation & Rationale</label>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      updateQuestionField(q.tempId, "showExplanation", false);
                                      updateQuestionField(q.tempId, "explanation", "");
                                    }}
                                    className="text-[9px] text-slate-400 hover:text-slate-600"
                                  >
                                    Remove
                                  </button>
                                </div>
                                <textarea
                                  placeholder="Provide step-by-step logical explanation..."
                                  value={q.explanation}
                                  onChange={e => updateQuestionField(q.tempId, "explanation", e.target.value)}
                                  className="w-full min-h-[50px] p-2.5 border border-slate-200 rounded-xl focus:outline-none focus:border-[#00CBA9] text-xs bg-slate-50/30"
                                />
                              </div>
                            )}

                            {/* Collapsible Advanced Meta (Image URL, Boards, Colleges) */}
                            <div className="border-t border-slate-50 pt-2">
                              <button
                                type="button"
                                onClick={() => updateQuestionField(q.tempId, "showAdvanced", !q.showAdvanced)}
                                className="flex items-center gap-1 text-[10px] font-bold text-slate-400 hover:text-slate-600 transition-colors"
                              >
                                {q.showAdvanced ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                {q.showAdvanced ? "Hide Advanced Options" : "Show Advanced Options (Image, Boards, Colleges)"}
                              </button>

                              {q.showAdvanced && (
                                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-1 duration-200 p-3 bg-slate-50/40 rounded-xl border border-slate-100">
                                  {/* Image URL */}
                                  <div className="sm:col-span-2">
                                    <label className="block text-[9px] font-bold text-gray-450 uppercase mb-1">Image URL (Optional)</label>
                                    <Input
                                      type="text"
                                      placeholder="https://example.com/image.png"
                                      value={q.image_url}
                                      onChange={e => updateQuestionField(q.tempId, "image_url", e.target.value)}
                                      className="py-1"
                                    />
                                  </div>

                                  {/* Boards repeater */}
                                  <div className="space-y-2">
                                    <label className="block text-[9px] font-bold text-gray-450 uppercase">Board Exam References</label>
                                    <div className="space-y-2">
                                      {q.boardRefs.map((ref, bIdx) => (
                                        <div key={bIdx} className="flex items-center gap-1">
                                          <Input
                                            type="text"
                                            placeholder="Board Name"
                                            value={ref.reference_board}
                                            onChange={e => {
                                              const nextRefs = [...q.boardRefs];
                                              nextRefs[bIdx].reference_board = e.target.value;
                                              updateQuestionField(q.tempId, "boardRefs", nextRefs);
                                            }}
                                            className="text-xs py-1"
                                          />
                                          <Input
                                            type="text"
                                            placeholder="Year"
                                            value={ref.reference_year}
                                            onChange={e => {
                                              const nextRefs = [...q.boardRefs];
                                              nextRefs[bIdx].reference_year = e.target.value;
                                              updateQuestionField(q.tempId, "boardRefs", nextRefs);
                                            }}
                                            className="text-xs w-16 py-1"
                                          />
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const nextRefs = q.boardRefs.filter((_, idx) => idx !== bIdx);
                                              updateQuestionField(q.tempId, "boardRefs", nextRefs);
                                            }}
                                            className="p-1 text-red-500 hover:text-red-750"
                                          >
                                            <Trash2 className="w-3.5 h-3.5" />
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const nextRefs = [...q.boardRefs, { reference_board: "", reference_year: "" }];
                                        updateQuestionField(q.tempId, "boardRefs", nextRefs);
                                      }}
                                      className="text-[9px] font-semibold text-[#00CBA9] flex items-center gap-0.5 hover:underline"
                                    >
                                      + Add Board
                                    </button>
                                  </div>

                                  {/* Colleges/Colleges tags */}
                                  <div className="space-y-2">
                                    <label className="block text-[9px] font-bold text-gray-450 uppercase">Institution References</label>
                                    <div className="flex items-center gap-1.5">
                                      <Input
                                        type="text"
                                        placeholder="Add college..."
                                        value={q.customInstInput}
                                        onChange={e => updateQuestionField(q.tempId, "customInstInput", e.target.value)}
                                        className="text-xs py-1"
                                      />
                                      <Button
                                        type="button"
                                        onClick={() => {
                                          if (!q.customInstInput.trim()) return;
                                          const nextInsts = [...q.instRefs, q.customInstInput.trim()];
                                          updateQuestionField(q.tempId, "instRefs", nextInsts);
                                          updateQuestionField(q.tempId, "customInstInput", "");
                                        }}
                                        className="py-1 px-2.5 text-[10px]"
                                      >
                                        Add
                                      </Button>
                                    </div>
                                    <div className="flex flex-wrap gap-1">
                                      {q.instRefs.map((inst, instIdx) => (
                                        <span key={instIdx} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[9px]">
                                          {inst}
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const nextInsts = q.instRefs.filter((_, idx) => idx !== instIdx);
                                              updateQuestionField(q.tempId, "instRefs", nextInsts);
                                            }}
                                            className="text-slate-400 hover:text-slate-600"
                                          >
                                            &times;
                                          </button>
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>

                          </div>
                        ))}
                      </div>

                      {/* Actions footer of builder */}
                      <div className="flex items-center justify-between border-t border-gray-150 pt-4 bg-white p-4 rounded-xl border shadow-sm">
                        <button
                          type="button"
                          onClick={handleAddQuestionCard}
                          className="inline-flex items-center gap-1 px-4 py-2 border border-[#00CBA9] text-[#00CBA9] hover:bg-[#00CBA9]/5 text-xs font-bold rounded-xl transition-all"
                        >
                          <Plus className="w-4 h-4" /> Add Question
                        </button>
                        
                        <Button
                          type="button"
                          onClick={handleCreateQuestions}
                          disabled={isSavingQuestion}
                          className="px-6 py-2.5 text-xs"
                        >
                          {isSavingQuestion ? "Saving all..." : "Save Questions & Add to Test"}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Mode Content: Bulk Import Action Trigger */}
                  {activeQuestionMode === 'import' && (
                    <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm text-center max-w-xl mx-auto space-y-4">
                      <div className="w-12 h-12 rounded-full bg-[#00CBA9]/10 flex items-center justify-center mx-auto">
                        <ExternalLink className="w-6 h-6 text-[#00CBA9]" />
                      </div>
                      <h4 className="font-bold text-gray-800 text-sm">Bulk Question Import (Excel)</h4>
                      <p className="text-xs text-gray-500">
                        To import questions from Excel sheets, use the dedicated Bulk Import module.
                        Once imported successfully, return here and click refresh to load the new questions!
                      </p>
                      
                      <div className="flex items-center justify-center gap-4 pt-2">
                        <a
                          href="/wp-admin/admin.php?page=shuddhoshikha-import"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#00CBA9] hover:bg-[#00b597] text-white text-xs font-semibold rounded-xl shadow-sm transition-all"
                        >
                          Open Bulk Import in New Tab <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                        
                        <button
                          type="button"
                          onClick={() => {
                            fetchQuestionsList();
                            showToast("Questions list refreshed", "success");
                          }}
                          className="inline-flex items-center gap-1.5 px-4 py-2 border border-gray-200 hover:bg-slate-50 text-gray-600 text-xs font-semibold rounded-xl shadow-sm transition-all"
                        >
                          Refresh Questions List <RefreshCw className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* STEP 3: Revision Section */}
              {wizardStep === 3 && (
                <div className="space-y-4 max-w-3xl mx-auto">
                  <div className="bg-white border border-gray-150 rounded-2xl p-6 shadow-sm">
                    <h4 className="text-sm font-bold text-gray-700 border-b border-gray-100 pb-3 flex items-center gap-1.5 mb-4">
                      <BookOpen className="w-4 h-4 text-[#00CBA9]" /> Step 3: Revision section - Review Questions Before Publishing
                    </h4>
                    
                    <div className="divide-y divide-gray-100 text-xs space-y-4">
                      {questions.filter(q => selectedQuestionIds.includes(q.id)).length === 0 ? (
                        <div className="p-8 text-center text-gray-400">No questions selected. Go back to Step 2 to select questions.</div>
                      ) : (
                        questions.filter(q => selectedQuestionIds.includes(q.id)).map((q, index) => (
                          <div key={q.id} className="pt-4 first:pt-0 space-y-3">
                            <div className="flex items-start justify-between gap-3">
                              <span className="font-bold text-gray-800">Question {index + 1}: {q.type === 'cq' ? 'Creative (CQ)' : 'Multiple Choice (MCQ)'}</span>
                              <button
                                type="button"
                                onClick={() => toggleQuestionSelect(q.id)}
                                className="text-red-500 hover:text-red-755 flex items-center gap-1 font-semibold text-[10px]"
                              >
                                <Trash2 className="w-3.5 h-3.5" /> Remove
                              </button>
                            </div>

                            {/* Stimulus Stem if any */}
                            {q.stem && (
                              <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 italic text-slate-600">
                                <strong>উদ্দীপক / Stem:</strong> {q.stem}
                              </div>
                            )}

                            {/* MCQ option choices display */}
                            {q.type !== 'cq' ? (
                              <div className="space-y-2 pl-2">
                                <p className="font-semibold text-gray-800">{q.question_text}</p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                  {q.options?.map((opt, oIdx) => {
                                    const isCorrect = q.correct_answer === oIdx.toString();
                                    return (
                                      <div key={oIdx} className={`p-2.5 rounded-lg border text-[11px] flex items-center gap-2 ${
                                        isCorrect ? 'border-emerald-200 bg-emerald-50/50 text-emerald-800 font-semibold' : 'border-gray-150 bg-white'
                                      }`}>
                                        <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] ${
                                          isCorrect ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-400'
                                        }`}>
                                          {String.fromCharCode(65 + oIdx)}
                                        </span>
                                        {opt}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ) : (
                              /* CQ sub-questions list */
                              <div className="space-y-2 pl-2 border-l-2 border-slate-200">
                                {renderQuestionText(q)}
                              </div>
                            )}

                            {q.explanation && (
                              <div className="p-2.5 bg-yellow-50/50 text-amber-900 border border-yellow-100 rounded-lg text-[11px]">
                                <strong>Explanation:</strong> {q.explanation}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 4: Finalize & Publish */}
              {wizardStep === 4 && (
                <div className="bg-white border border-gray-150 rounded-2xl p-6 shadow-sm max-w-xl mx-auto space-y-6">
                  <h4 className="text-sm font-bold text-gray-700 border-b border-gray-100 pb-3 flex items-center gap-1.5">
                    <Check className="w-4 h-4 text-[#00CBA9]" /> Step 4: Final Review & Publish
                  </h4>

                  <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100 space-y-4 text-xs">
                    <div className="flex justify-between border-b border-slate-100 pb-2">
                      <span className="text-gray-400 uppercase font-bold text-[9px]">Test Title</span>
                      <span className="font-semibold text-gray-800">{title}</span>
                    </div>
                    <div className="flex justify-between border-b border-slate-100 pb-2">
                      <span className="text-gray-400 uppercase font-bold text-[9px]">Academic Info</span>
                      <span className="font-semibold text-gray-800 uppercase">{level} • {group}</span>
                    </div>
                    <div className="flex justify-between border-b border-slate-100 pb-2">
                      <span className="text-gray-400 uppercase font-bold text-[9px]">Duration / Marks</span>
                      <span className="font-semibold text-gray-800">{duration} Mins • {marks} Marks</span>
                    </div>
                    <div className="flex justify-between border-b border-slate-100 pb-2">
                      <span className="text-gray-400 uppercase font-bold text-[9px]">Negative Mark Rate</span>
                      <span className="font-semibold text-gray-800">-{negMark}</span>
                    </div>
                    <div className="flex justify-between border-b border-slate-100 pb-2">
                      <span className="text-gray-400 uppercase font-bold text-[9px]">Selected Questions</span>
                      <span className="font-bold text-[#00CBA9]">{selectedQuestionIds.length} Questions</span>
                    </div>
                    <div className="flex justify-between border-b border-slate-100 pb-2">
                      <span className="text-gray-400 uppercase font-bold text-[9px]">Start Date (UTC+6)</span>
                      <span className="font-semibold text-gray-800">{startTime.replace("T", " ")}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400 uppercase font-bold text-[9px]">End Date (UTC+6)</span>
                      <span className="font-semibold text-gray-800">{endTime.replace("T", " ")}</span>
                    </div>
                  </div>

                  <p className="text-[11px] text-gray-500 text-center italic">
                    By clicking publish, this model test will be scheduled and made available for students matching the academic settings.
                  </p>
                </div>
              )}

            </div>

            {/* Wizard Navigation Footer */}
            <div className="bg-white border-t border-gray-150 px-6 py-4 flex items-center justify-between">
              <Button
                type="button"
                variant="secondary"
                disabled={wizardStep === 1}
                onClick={handleBackStep}
                leftIcon={<ArrowLeft className="w-4 h-4" />}
              >
                Back
              </Button>

              <div className="flex items-center gap-3">
                <Button type="button" variant="secondary" onClick={() => setShowWizard(false)}>
                  Cancel
                </Button>
                {wizardStep < 4 ? (
                  <Button
                    type="button"
                    onClick={handleNextStep}
                    rightIcon={<ArrowRight className="w-4 h-4" />}
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={handleCreateTest}
                    leftIcon={<Check className="w-4 h-4" />}
                  >
                    Publish Model Test
                  </Button>
                )}
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}
